import { useEffect, useMemo, useState, useCallback } from 'react';
import { api } from '../../api/client';
import { fmtLocalDate } from '../../utils/datetime';
import { FormTitle } from '../../components/FormTitle';
import {
  type Kpis, type DailyCount, type TopClient, type StageRow, type Unit,
  fmt, buildDonut, weekOverWeek, axisLabel, sparkPoints, clientShare, onTimePct,
  pipelineStages, barWidths, buildAlerts, activeAlertCount,
} from './executiveDashboardLogic';
import './executive.css';

/* ── Drill-down detail ──────────────────────────────────────────── */

interface DetailCol { key: string; label: string; isDate?: boolean; highlight?: 'jobid' | 'overdue' }
interface DetailCfg { title: string; icon: string; url: string; cols: DetailCol[]; limit?: number }

type DetailRow = Record<string, unknown>;

const JOB_COLS: DetailCol[] = [
  { key: 'jobId',        label: 'Job ID',        highlight: 'jobid' },
  { key: 'client',       label: 'Client'  },
  { key: 'projectId',    label: 'Project' },
  { key: 'receivedDate', label: 'Received', isDate: true },
  { key: 'dueDate',      label: 'Due Date', isDate: true, highlight: 'overdue' },
  { key: 'statusName',   label: 'Status'  },
];

const TEST_COLS: DetailCol[] = [
  { key: 'jobId',       label: 'Job ID',    highlight: 'jobid' },
  { key: 'sampleId',    label: 'Sample ID' },
  { key: 'testName',    label: 'Test'      },
  { key: 'method',      label: 'Method'    },
  { key: 'analyzedDate',label: 'Analyzed', isDate: true },
  { key: 'analyzedBy',  label: 'By'        },
];

/** Drill-down targets shared by the pipeline, tiles and hero stats. */
const DRILL: Record<NonNullable<StageRow['drill']>, DetailCfg> = {
  'due-today':          { title: 'Jobs Due Today',                 icon: 'ti-clock-exclamation', url: '/api/v1/dashboard/detail/samples-due?days=1', cols: JOB_COLS },
  'pending-validation': { title: 'Tests Pending Result Validation', icon: 'ti-check',             url: '/api/v1/dashboard/detail/pending-validation', cols: TEST_COLS, limit: 500 },
  'pending-approval':   { title: 'Tests Pending Result Approval',   icon: 'ti-checks',            url: '/api/v1/dashboard/detail/pending-approval',   cols: TEST_COLS, limit: 500 },
  'pending-reporting':  { title: 'Open Jobs Pending Reporting',     icon: 'ti-report',            url: '/api/v1/dashboard/detail/pending-reporting',  cols: JOB_COLS },
};

// Render via the shared §14b.6 helper (local tz, MM/DD/YYYY); keep the '—'
// placeholder for empty values used in the detail-modal date columns.
const fmtDate = (v: unknown) => {
  if (!v) return '—';
  return fmtLocalDate(v as string) || String(v);
};

function DetailModal({ cfg, onClose }: { cfg: DetailCfg; onClose: () => void }) {
  const [rows,    setRows]    = useState<DetailRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err,     setErr]     = useState<string | null>(null);

  useEffect(() => {
    setLoading(true); setErr(null); setRows([]);
    api.get(cfg.url)
      .then(r => setRows((r.data?.data ?? []) as DetailRow[]))
      .catch(() => setErr('Failed to load detail data.'))
      .finally(() => setLoading(false));
  }, [cfg.url]);

  const today = new Date(); today.setHours(0,0,0,0);
  const capped = cfg.limit != null && rows.length >= cfg.limit;

  return (
    <div className="exec-modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="exec-modal">
        <div className="exec-modal-hdr">
          <div className="exec-modal-title">
            <i className={`ti ${cfg.icon}`} />
            {cfg.title}
            {!loading && (
              <span className="exec-modal-count">
                {capped ? `first ${fmt(rows.length)} shown` : `${fmt(rows.length)} record${rows.length !== 1 ? 's' : ''}`}
              </span>
            )}
          </div>
          <button className="exec-modal-close" onClick={onClose}><i className="ti ti-x" /></button>
        </div>
        <div className="exec-modal-body">
          {loading && <div className="exec-modal-loading"><i className="ti ti-loader" /> Loading…</div>}
          {err     && <div className="exec-modal-loading" style={{ color:'#c45a08' }}>{err}</div>}
          {!loading && !err && rows.length === 0 && <div className="exec-modal-empty">No records found.</div>}
          {!loading && !err && rows.length > 0 && (
            <table className="exec-detail-tbl">
              <thead>
                <tr>{cfg.cols.map(c => <th key={c.key}>{c.label}</th>)}</tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i}>
                    {cfg.cols.map(c => {
                      const val = row[c.key];
                      const display = c.isDate ? fmtDate(val) : (val == null || val === '' ? '—' : String(val));
                      let cls = '';
                      if (c.highlight === 'jobid') cls = 'jobid';
                      else if (c.highlight === 'overdue' && val) {
                        const d = new Date(val as string); d.setHours(0,0,0,0);
                        if (d < today) cls = 'overdue';
                      }
                      return <td key={c.key} className={cls}>{display}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Bar list (pipeline / results) — bars compare only like units ── */
interface BarRow { key: string; label: string; count: number; unit: Unit; color: string; onClick?: () => void }

function BarList({ rows }: { rows: BarRow[] }) {
  const widths = barWidths(rows);
  return (
    <>
      {rows.map((row, i) => (
        <div
          key={row.key}
          className={`exec-pipe-row${row.onClick ? ' click' : ''}`}
          onClick={row.onClick}
          title={row.onClick ? 'Click to view the list' : undefined}
        >
          <div className="exec-pipe-lbl">{row.label}</div>
          <div className="exec-pipe-cnt" style={{ color: row.color }}>{fmt(row.count)}</div>
          <div className="exec-pipe-bar">
            <div className="exec-pipe-fill" style={{ width: `${widths[i] ?? 0}%`, background: row.color }} />
          </div>
          <div className="exec-pipe-pct">{row.unit}</div>
        </div>
      ))}
    </>
  );
}

/* ── Component ───────────────────────────────────────────────── */
export function ExecutiveDashboardPage({ onHome }: { onHome?: () => void }) {
  const [kpis,    setKpis]    = useState<Kpis | null>(null);
  const [trend,   setTrend]   = useState<DailyCount[]>([]);
  const [clients, setClients] = useState<TopClient[]>([]);
  const [busy,    setBusy]    = useState(true);
  const [loadMs,  setLoadMs]  = useState<number | null>(null);
  const [err,     setErr]     = useState<string | null>(null);
  const [detail,  setDetail]  = useState<DetailCfg | null>(null);

  const openDetail = useCallback((cfg: DetailCfg) => setDetail(cfg), []);
  const closeDetail = useCallback(() => setDetail(null), []);

  const now = new Date();
  /* calendar-header label — permitted §18.6 exception (hero eyebrow month-name
     heading, not a data value) */
  const dateLabel = now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const refresh = () => {
    setBusy(true); setErr(null);
    const t0 = performance.now();
    Promise.all([
      api.get('/api/v1/dashboard/kpis'),
      api.get('/api/v1/dashboard/samples-per-day', { params: { days: 14 } }),
      api.get('/api/v1/dashboard/top-clients',     { params: { days: 90, take: 7 } }),
    ])
      .then(([k, s, c]) => {
        setKpis(k.data?.data ?? null);
        setTrend((s.data?.data ?? []) as DailyCount[]);
        setClients((c.data?.data ?? []) as TopClient[]);
      })
      .catch(() => setErr('Could not load dashboard data.'))
      .finally(() => { setLoadMs(performance.now() - t0); setBusy(false); });
  };
  useEffect(refresh, []);

  /* Derived values — all pure, see executiveDashboardLogic.ts */
  const donut    = useMemo(() => (kpis ? buildDonut(kpis) : null), [kpis]);
  const points   = useMemo(() => sparkPoints(trend), [trend]);
  const alerts   = useMemo(() => (kpis ? buildAlerts(kpis) : []), [kpis]);
  const wow      = useMemo(() => weekOverWeek(trend), [trend]);
  const onTime   = kpis ? onTimePct(kpis) : null;
  const todayCnt = trend.at(-1)?.count ?? null;

  const pipeline: BarRow[] = kpis
    ? pipelineStages(kpis).map(s => ({ ...s, onClick: s.drill ? () => openDetail(DRILL[s.drill!]) : undefined }))
    : [];

  const results: BarRow[] = kpis ? [
    { key: 'val',  label: 'Pending Result Validation', count: kpis.pendingValidation,     unit: 'tests',   color: '#534AB7', onClick: () => openDetail(DRILL['pending-validation']) },
    { key: 'appr', label: 'Pending Result Approval',   count: kpis.pendingResultApproval, unit: 'tests',   color: '#c45a08', onClick: () => openDetail(DRILL['pending-approval']) },
    { key: 'rep',  label: 'Pending Reporting',         count: kpis.pendingReporting,      unit: 'jobs',    color: '#e8a014', onClick: () => openDetail(DRILL['pending-reporting']) },
    { key: 'sdmp', label: 'SDMS Pending Review',       count: kpis.sdmsPendingReview,     unit: 'batches', color: '#e8a014' },
    { key: 'sdmv', label: 'SDMS Validated',            count: kpis.sdmsValidated,         unit: 'batches', color: '#0F6E56' },
  ] : [];

  const heroTitle = !kpis
    ? 'Loading lab overview…'
    : kpis.overdue > 0
      ? `${fmt(kpis.openJobs)} active jobs · ${fmt(kpis.overdue)} overdue`
      : `${fmt(kpis.openJobs)} active jobs — all on track`;

  return (
    <div className="exec-page">

      {/* ── Top bar ── */}
      <div className="dp-topbar">
        <div className="dp-topbar-row">
          <div>
            <div className="dp-breadcrumb">
              {onHome ? <a onClick={onHome}>Home</a> : null}
              {onHome ? <i className="ti ti-chevron-right" /> : null}
              <span>Executive Dashboard</span>
            </div>
            {/* Shared FormTitle — the ONE page heading (§18.6): nav icon/colour
                via navMeta('execdash') + the TOP load-time chip. */}
            <FormTitle pageId="execdash" loadMs={loadMs} loading={busy} />
            <div className="dp-sub">
              Live overview · As of {fmtLocalDate(now.toISOString())} ·{' '}
              <a onClick={refresh} style={{ pointerEvents: busy ? 'none' : 'auto' }}>
                {busy ? 'loading…' : 'Refresh'}
              </a>
            </div>
          </div>
          {/* Shared .ca-btn + prototype glyph (BO UI Standard §7) — read-only
              dashboard, so Refresh is the only action. */}
          <button className="ca-btn" onClick={refresh} disabled={busy}>
            <span className="tb-i tb-i-green">🔄</span> Refresh
          </button>
        </div>
      </div>

      {/* ── Scroll area ── */}
      <div className="dp-scroll">

        {err && <div className="exec-err">{err}</div>}

        {/* ── Hero banner ── */}
        <div className="exec-hero">
          <div className="exec-hero-left">
            <div className="exec-hero-ey">ALPACA LIMS G6 · {dateLabel}</div>
            <div className="exec-hero-t">{heroTitle}</div>
            <div className="exec-hero-s">
              {kpis
                ? `${fmt(kpis.activeClients)} of ${fmt(kpis.totalClients)} clients active (90d) · ${fmt(kpis.activeProjects)} active projects · Read-only live mirror`
                : 'Live ALPACA database · Read-only mirror'}
            </div>
          </div>
          <div className="exec-hero-stats">
            <div className="exec-hero-stat exec-hero-stat-click" title="Click to view open jobs"
              onClick={() => openDetail({ title: 'Active (Open) Jobs', icon: 'ti-briefcase', url: '/api/v1/dashboard/detail/open-jobs', cols: JOB_COLS })}>
              <div className="exec-hero-stat-val">{fmt(kpis?.openJobs)}</div>
              <div className="exec-hero-stat-lbl">Active jobs</div>
            </div>
            <div className="exec-hero-div" />
            <div className="exec-hero-stat" title="Every SDMS analytical batch with a test, all time">
              <div className="exec-hero-stat-val g">{fmt(kpis?.qcBatches)}</div>
              <div className="exec-hero-stat-lbl">QC batches (all)</div>
            </div>
            <div className="exec-hero-div" />
            <div className="exec-hero-stat" title="Clients with a job received in the last 90 days">
              <div className="exec-hero-stat-val o">{fmt(kpis?.activeClients)}</div>
              <div className="exec-hero-stat-lbl">Active clients (90d)</div>
            </div>
            <div className="exec-hero-div" />
            <div className="exec-hero-stat exec-hero-stat-click" title="Click to view overdue jobs"
              onClick={() => openDetail({ title: 'Overdue Jobs', icon: 'ti-alert-octagon', url: '/api/v1/dashboard/detail/overdue', cols: JOB_COLS })}>
              <div className={`exec-hero-stat-val${(kpis?.overdue ?? 0) > 0 ? ' r' : ' g'}`}>
                {fmt(kpis?.overdue)}
              </div>
              <div className="exec-hero-stat-lbl">Overdue</div>
            </div>
          </div>
        </div>

        {/* ── 4 KPI tiles ── */}
        <div className="exec-kpi-row">

          {/* Tile 1 — Total Jobs (30d) — blue bar */}
          <div className="exec-kpi exec-kpi-clickable"
            onClick={() => openDetail({ title: 'Jobs Received — Last 30 Days', icon: 'ti-briefcase', url: '/api/v1/dashboard/detail/recent-jobs?days=30', cols: JOB_COLS })}>
            <div className="exec-kpi-bar" />
            <div className="exec-kpi-lbl">Total Jobs (30d)</div>
            <div className="exec-kpi-val">{fmt(kpis?.totalJobs30d)}</div>
            {wow != null ? (
              <div className={`exec-kpi-trend ${wow > 0 ? 'up' : wow < 0 ? 'down' : 'flat'}`}>
                {wow !== 0 && <i className={`ti ${wow > 0 ? 'ti-trending-up' : 'ti-trending-down'}`} />}
                {wow > 0 ? '+' : ''}{wow} jobs this week vs last
              </div>
            ) : (
              <div className="exec-kpi-trend flat">—</div>
            )}
          </div>

          {/* Tile 2 — Samples Due (3d) — red bar — format: Tests / Samples / Clients */}
          <div className="exec-kpi exec-kpi-clickable"
            onClick={() => openDetail({ title: 'Jobs Due — Next 3 Days', icon: 'ti-clock-exclamation', url: '/api/v1/dashboard/detail/samples-due?days=3', cols: JOB_COLS })}>
            <div className="exec-kpi-bar r" />
            <div className="exec-kpi-lbl">Samples Due (3d)</div>
            <div className="exec-kpi-val">
              {kpis
                ? `${fmt(kpis.samplesDue3dTests)} / ${fmt(kpis.samplesDue3dSamples)} / ${fmt(kpis.samplesDue3dClients)}`
                : '—'}
            </div>
            <div className="exec-kpi-sub">Tests / Samples / Clients</div>
          </div>

          {/* Tile 3 — Pending Validation — amber bar */}
          <div className="exec-kpi exec-kpi-clickable"
            onClick={() => openDetail(DRILL['pending-validation'])}>
            <div className="exec-kpi-bar a" />
            <div className="exec-kpi-lbl">Pending Validation</div>
            <div className="exec-kpi-val">{fmt(kpis?.pendingValidation)}</div>
            <div className="exec-kpi-trend flat">
              tests · {fmt(kpis?.sdmsPendingReview)} SDMS batch{kpis?.sdmsPendingReview === 1 ? '' : 'es'} pending
            </div>
          </div>

          {/* Tile 4 — Reports Delivered (7d) — green bar */}
          <div className="exec-kpi exec-kpi-clickable"
            onClick={() => openDetail({ title: 'Reports Delivered — Last 7 Days', icon: 'ti-file-check', url: '/api/v1/dashboard/detail/reports-delivered?days=7', cols: JOB_COLS })}>
            <div className="exec-kpi-bar g" />
            <div className="exec-kpi-lbl">Reports Delivered (7d)</div>
            <div className="exec-kpi-val">{fmt(kpis?.reportsDelivered7d)}</div>
            {onTime != null ? (
              <div className={`exec-kpi-trend ${onTime >= 90 ? 'up' : 'down'}`}>
                <i className={`ti ${onTime >= 90 ? 'ti-circle-check' : 'ti-alert-triangle'}`} />
                {fmt(kpis?.reportsDeliveredOnTime7d)} on time ({onTime}%)
              </div>
            ) : (
              <div className="exec-kpi-trend flat">No reports this week</div>
            )}
          </div>

        </div>

        {/* ── Pipeline + Donut ── */}
        <div className="exec-row r-2">

          {/* Sample Backlog Pipeline */}
          <div className="exec-card">
            <div className="exec-card-hdr">
              <div className="exec-card-title"><i className="ti ti-stack" /> Sample Backlog Pipeline</div>
              <span style={{ fontSize: 10, color: 'var(--ai-muted)' }}>Bars compare like units · click a highlighted stage</span>
            </div>
            <div className="exec-card-body">
              <BarList rows={pipeline} />
            </div>
          </div>

          {/* Job Status Donut */}
          <div className="exec-card">
            <div className="exec-card-hdr">
              <div className="exec-card-title"><i className="ti ti-chart-pie" /> Job Status Distribution</div>
              <span style={{ fontSize: 10, color: 'var(--ai-muted)' }}>{fmt(kpis?.openJobs)} open jobs</span>
            </div>
            <div className="exec-card-body">
              <div className="exec-donut-wrap">
                <div
                  className="exec-donut"
                  style={{ background: donut?.gradient ?? 'conic-gradient(#ddd 0% 100%)' }}
                >
                  <div className="exec-donut-center">
                    <div className="exec-donut-center-val">{fmt(donut?.total)}</div>
                    <div className="exec-donut-center-lbl">Total</div>
                  </div>
                </div>
                <div className="exec-donut-legend">
                  {donut?.segments.map((seg) => (
                    <div key={seg.label} className="exec-donut-li">
                      <span className="exec-donut-dot" style={{ background: seg.color }} />
                      <span className="exec-donut-name">{seg.label}</span>
                      <span className="exec-donut-val">{fmt(seg.val)}</span>
                      <span className="exec-donut-pct">{seg.pct}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* ── Top Clients + Jobs trend ── */}
        <div className="exec-row r-12">

          {/* Top Clients */}
          <div className="exec-card">
            <div className="exec-card-hdr">
              <div className="exec-card-title"><i className="ti ti-trophy" /> Top Clients by Jobs</div>
              <span style={{ fontSize: 10, color: 'var(--ai-muted)' }}>
                Last 3 months{clients[0] ? ` · % of ${fmt(clients[0].totalJobs)} jobs` : ''}
              </span>
            </div>
            <div className="exec-card-body">
              {clients.length === 0 && !busy ? (
                <div style={{ color: 'var(--ai-muted)', fontSize: 12, fontStyle: 'italic' }}>No jobs in this window.</div>
              ) : (
                <table className="exec-mini">
                  <tbody>
                    {clients.map((c, i) => (
                      <tr key={c.name + i}>
                        <td>{c.name}</td>
                        <td className="r">
                          {fmt(c.count)}
                          <span className="pct">{clientShare(c)}%</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Jobs received per day */}
          <div className="exec-card">
            <div className="exec-card-hdr">
              <div className="exec-card-title"><i className="ti ti-chart-line" /> Jobs Received · 14 days</div>
              <span style={{ fontSize: 10, color: '#0F6E56', fontWeight: 'bold' }}>
                {todayCnt != null ? `${fmt(todayCnt)} today` : '—'}
              </span>
            </div>
            <div className="exec-card-body">
              <div className="exec-spark-wrap">
                <svg className="exec-spark" viewBox="0 0 400 70" preserveAspectRatio="none">
                  {points.length > 0 && (
                    <polyline
                      points={`${points.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')} 400,70 0,70`}
                      fill="#5DCAA5" opacity="0.18"
                    />
                  )}
                  {points.length > 0 && (
                    <polyline
                      points={points.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')}
                      fill="none"
                      stroke="#0F6E56"
                      strokeWidth="1.8"
                      strokeLinejoin="round"
                      strokeLinecap="round"
                    />
                  )}
                  {points.map((p, i) => {
                    const last = i === points.length - 1;
                    return (
                      <circle
                        key={i}
                        cx={p.x.toFixed(1)}
                        cy={p.y.toFixed(1)}
                        r={last ? '2.5' : '2'}
                        fill="#0F6E56"
                        stroke={last ? '#fff' : 'none'}
                        strokeWidth="1"
                      >
                        <title>{`${axisLabel(trend[i]?.date)}: ${trend[i]?.count} jobs`}</title>
                      </circle>
                    );
                  })}
                </svg>
                <div className="exec-spark-x">
                  {trend.filter((_, i) => i % 2 === 0 || i === trend.length - 1).map((d, i) => (
                    <span key={i}>{axisLabel(d.date)}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* ── Results & Reporting + Alerts ── */}
        <div className="exec-row r-2">

          <div className="exec-card">
            <div className="exec-card-hdr">
              <div className="exec-card-title"><i className="ti ti-report-analytics" /> Results &amp; Reporting</div>
            </div>
            <div className="exec-card-body">
              <BarList rows={results} />
            </div>
          </div>

          {/* Alerts & Exceptions */}
          <div className="exec-card">
            <div className="exec-card-hdr">
              <div className="exec-card-title"><i className="ti ti-bell" /> Alerts &amp; Exceptions</div>
              <span style={{ fontSize: 10, color: '#c45a08', fontWeight: 'bold' }}>
                {activeAlertCount(alerts)} active
              </span>
            </div>
            <div className="exec-card-body">
              {alerts.map((a, i) => (
                <div key={i} className={`exec-alert${a.kind ? ` ${a.kind}` : ''}`}>
                  <i className={`ti ${a.icon}`} />
                  <div className="exec-alert-body">
                    <div className="exec-alert-t">{a.title}</div>
                    <div className="exec-alert-s">{a.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* ── Bottom 3-column row ── */}
        <div className="exec-row r-3">

          {/* Workflow Stage Counts */}
          <div className="exec-card">
            <div className="exec-card-hdr">
              <div className="exec-card-title"><i className="ti ti-list-check" /> Workflow Stages</div>
            </div>
            <div className="exec-card-body">
              {kpis && [
                { label: 'Pending Submission',  count: kpis.pendingSubmission,     unit: 'jobs',    color: '#7c3aed' },
                { label: 'Sample Prep Backlog', count: kpis.samplePrepBacklog,     unit: 'tests',   color: '#0ea5e9' },
                { label: 'SDMS Pending Review', count: kpis.sdmsPendingReview,     unit: 'batches', color: '#e07b00' },
                { label: 'Pending Validation',  count: kpis.pendingValidation,     unit: 'tests',   color: '#f59e0b' },
                { label: 'Result Approval',     count: kpis.pendingResultApproval, unit: 'tests',   color: '#16a34a' },
                { label: 'Pending Reporting',   count: kpis.pendingReporting,      unit: 'jobs',    color: '#dc2626' },
              ].map((row) => (
                <div key={row.label} className="exec-pipe-row no-bar">
                  <div className="exec-pipe-lbl" style={{ minWidth: 'auto', flex: 1 }}>{row.label}</div>
                  <div className="exec-pipe-cnt" style={{ color: row.color }}>{fmt(row.count)}</div>
                  <div className="exec-pipe-unit">{row.unit}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Lab Overview */}
          <div className="exec-card">
            <div className="exec-card-hdr">
              <div className="exec-card-title"><i className="ti ti-building" /> Lab Overview</div>
            </div>
            <div className="exec-card-body">
              {kpis && [
                { label: 'Active Clients (90d)',    count: kpis.activeClients,  color: '#185FA5' },
                { label: 'Registered Clients',      count: kpis.totalClients,   color: '#185FA5' },
                { label: 'Active Projects',         count: kpis.activeProjects, color: '#185FA5' },
                { label: 'Samples (all time)',      count: kpis.totalSamples,   color: '#0F6E56' },
                { label: 'Tests (all time)',        count: kpis.totalTests,     color: '#534AB7' },
                { label: 'QC Batches (all time)',   count: kpis.qcBatches,      color: '#e8a014' },
              ].map((row) => (
                <div key={row.label} className="exec-pipe-row no-bar">
                  <div className="exec-pipe-lbl" style={{ minWidth: 'auto', flex: 1 }}>{row.label}</div>
                  <div className="exec-pipe-cnt" style={{ color: row.color }}>{fmt(row.count)}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Top Clients Summary */}
          <div className="exec-card">
            <div className="exec-card-hdr">
              <div className="exec-card-title"><i className="ti ti-trophy" /> Top Clients (90d)</div>
            </div>
            <div className="exec-card-body">
              <table className="exec-mini">
                <tbody>
                  {clients.slice(0, 6).map((c, i) => (
                    <tr key={i}>
                      <td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.name}
                      </td>
                      <td className="r">
                        {fmt(c.count)}
                        <span className="pct">{clientShare(c)}%</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="exec-footer">
          ALPACA LIMS G6 · Executive Dashboard · powered by BT·Soft &nbsp;·&nbsp; v26.11.3
        </div>

      </div>{/* dp-scroll */}

      {/* ── Detail Modal (portal-like, outside scroll area) ── */}
      {detail && <DetailModal cfg={detail} onClose={closeDetail} />}

    </div>
  );
}
